import type { GridSortModel } from "@mui/x-data-grid-pro"
import type { BiothreatSignal } from "@phc/common/gen/threatAnalyticsGen"
import type { DynamicDateRange } from "../../utils/dates"

export type { NwssSignalSample, NwssSignalSampleList } from "./nwssSampleTypes"

export type PrecomputedBiothreatSignal = BiothreatSignal

export type PrecomputedTab = "gdelt" | "nwss"

/**
 * Scan-map sweep behavior. `auto` continuously sweeps and resolves transitions
 * as the line passes; `onDemand` parks the line and runs a single pass only when
 * a filter or the impact threshold changes.
 */
export type PrecomputedScanMode = "auto" | "onDemand"

export interface PrecomputedGdeltFilters {
  disease: string
  state: string
  country: string
  impactScoreMin: string
  titleSearch: string
  memberTitleSearch: string
  summarySearch: string
}

export interface PrecomputedNwssFilters {
  disease: string
  state: string
}

export interface PrecomputedBiothreatSignalsState {
  /** Whether the "Selected Signals" panel is expanded. Persisted; default off. */
  selectedSignalsExpanded: boolean
  setSelectedSignalsExpanded: (expanded: boolean) => void
  /** Rows-per-page for the signals grid. Persisted; default 25. */
  pageSize: number
  setPageSize: (pageSize: number) => void
  /** User-dragged width (px) of the v2 scan-map rail; null = use default. */
  mapRailWidth: number | null
  setMapRailWidth: (width: number) => void
  /** Scan-map sweep behavior (auto continuous vs on-demand). Persisted. */
  scanMode: PrecomputedScanMode
  setScanMode: (mode: PrecomputedScanMode) => void
  activeTab: PrecomputedTab
  setActiveTab: (tab: PrecomputedTab) => void
  dateRange: DynamicDateRange
  setDateRange: (dateRange: DynamicDateRange) => void
  gdeltFilters: PrecomputedGdeltFilters
  setGdeltFilters: (filters: PrecomputedGdeltFilters) => void
  resetGdeltFilters: () => void
  nwssFilters: PrecomputedNwssFilters
  setNwssFilters: (filters: PrecomputedNwssFilters) => void
  resetNwssFilters: () => void
  gdeltSortModel: GridSortModel
  setGdeltSortModel: (sortModel: GridSortModel) => void
  nwssSortModel: GridSortModel
  setNwssSortModel: (sortModel: GridSortModel) => void
  pinnedSignals: Map<string, PrecomputedBiothreatSignal>
  addPinnedSignal: (signal: PrecomputedBiothreatSignal) => void
  removePinnedSignal: (signalId: string) => void
}

export const ALL_DISEASES = "All diseases"
export const ALL_GEO = "All"
